import React, { useEffect, useState, useMemo } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Trash2, Mail, Phone, Calendar, Crown, Star, X, Filter, Send, MessageSquarePlus, AlertCircle } from 'lucide-react';

const TYPE_LABELS = {
  initial: 'Demande initiale',
  weddings: 'Questionnaire Weddings',
  receptions: 'Questionnaire Réceptions & Corporate',
  heritage: 'Questionnaire Héritage',
};


const STATUSES = [
  { value: 'new', label: 'Nouvelle' },
  { value: 'in_review', label: 'En cours' },
  { value: 'answered', label: 'Répondue' },
  { value: 'archived', label: 'Archivée' },
];

const STATUS_COLORS = {
  new: 'bg-[#111111] text-white',
  in_review: 'bg-blue-100 text-blue-800',
  answered: 'bg-green-100 text-green-800',
  archived: 'bg-gray-200 text-gray-700',
};

const formatDate = (d) => {
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const QuotesManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailStatus, setEmailStatus] = useState({ configured: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/quotes');
      setItems(data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get('/admin/email-status').then(({ data }) => setEmailStatus(data)).catch(() => {});
  }, []);

  // Keep selected in sync when items reload
  useEffect(() => {
    if (selected) {
      const fresh = items.find((q) => q.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  }, [items]);  // eslint-disable-line

  const filtered = useMemo(() => {
    return items.filter((q) => {
      if (typeFilter !== 'all' && q.submissionType !== typeFilter) return false;
      if (statusFilter !== 'all' && q.status !== statusFilter) return false;
      return true;
    });
  }, [items, typeFilter, statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, { status });
      setItems((arr) => arr.map((q) => (q.id === id ? data : q)));
      toast.success('Statut mis à jour');
    } catch { toast.error('Erreur'); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setItems((arr) => arr.filter((q) => q.id !== id));
      if (selected && selected.id === id) setSelected(null);
      toast.success('Demande supprimée');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="admin-quotes-manager">
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Demandes reçues</span>
          <h2 className="text-4xl font-serif text-[#111111]">Devis ({filtered.length})</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <Filter className="w-4 h-4 text-[#6B6B6B]" />
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="quotes-type-filter" className="w-full sm:w-auto max-w-full border border-[#111111]/20 px-4 py-2 uppercase tracking-widest bg-white">
            <option value="all">Tous types</option>
            <option value="initial">Demande initiale</option>
            <option value="weddings">Questionnaire Weddings</option>
            <option value="receptions">Questionnaire Réceptions & Corporate</option>
            <option value="heritage">Questionnaire Héritage</option>
            <option value="private">Célébrations (ancien)</option>
            <option value="prestige">Prestige (ancien)</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="quotes-status-filter" className="w-full sm:w-auto max-w-full border border-[#111111]/20 px-4 py-2 uppercase tracking-widest bg-white">
            <option value="all">Tous statuts</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {!emailStatus.configured && (
        <div className="mb-6 border border-[#111111]/30 bg-[#111111]/10 px-5 py-3 text-xs text-[#111111] flex items-center gap-3" data-testid="email-not-configured">
          <AlertCircle className="w-4 h-4 text-[#111111] shrink-0" />
          <span>L'envoi d'emails depuis l'admin sera actif dès que la clé Resend (<code>RESEND_API_KEY</code>) sera ajoutée aux secrets.</span>
        </div>
      )}

      {loading ? (
        <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#111111]/20">
          <p className="text-[#6B6B6B]">Aucune demande pour ces filtres.</p>
        </div>
      ) : (
        <div className="border border-[#111111]/10 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-[0.25em] text-[#6B6B6B]">
              <tr>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Nom</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Événement</th>
                <th className="text-left p-4">Date évt.</th>
                <th className="text-left p-4">Reçu le</th>
                <th className="text-left p-4">Statut</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} className="border-t border-[#111111]/10 hover:bg-[#FFFFFF] cursor-pointer" data-testid={`quote-row-${q.id}`} onClick={() => setSelected(q)}>
                  <td className="p-4">
                    {TYPE_LABELS[q.submissionType] ? (
                      <span className="inline-flex items-center gap-1 text-[#111111]" data-testid={`quote-type-${q.id}`}>{TYPE_LABELS[q.submissionType]}</span>
                    ) : q.submissionType === 'prestige' ? (
                      <span className="inline-flex items-center gap-1 text-[#111111]"><Crown className="w-3 h-3" /> Prestige</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#111111]"><Star className="w-3 h-3" /> Privé</span>
                    )}
                  </td>
                  <td className="p-4 font-serif">{q.firstName} {q.lastName}</td>
                  <td className="p-4 text-[#6B6B6B]">{q.email}</td>
                  <td className="p-4">{q.eventType || '—'}</td>
                  <td className="p-4">{q.date || '—'}</td>
                  <td className="p-4 text-[#6B6B6B] text-xs">{formatDate(q.createdAt)}</td>
                  <td className="p-4">
                    <select
                      value={q.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      data-testid={`quote-status-${q.id}`}
                      className={`px-3 py-1 text-[10px] uppercase tracking-widest border-0 ${STATUS_COLORS[q.status] || 'bg-gray-100'}`}
                    >
                      {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={(e) => { e.stopPropagation(); del(q.id); }} data-testid={`quote-delete-${q.id}`} className="text-[#6B6B6B] hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <QuoteDetail
          quote={selected}
          onClose={() => setSelected(null)}
          onUpdate={(updated) => {
            setItems((arr) => arr.map((q) => (q.id === updated.id ? updated : q)));
          }}
          onDelete={() => del(selected.id)}
          onStatusChange={(v) => updateStatus(selected.id, v)}
          emailConfigured={emailStatus.configured}
        />
      )}
    </div>
  );
};

/* ============================== Detail ============================== */

const QuoteDetail = ({ quote, onClose, onUpdate, onDelete, onStatusChange, emailConfigured }) => {
  const [tab, setTab] = useState('details');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto border-t-4 border-[#111111] relative overflow-x-hidden" onClick={(e) => e.stopPropagation()} data-testid="quote-detail">
        <button onClick={onClose} aria-label="Fermer" data-testid="quote-detail-close" className="absolute top-5 right-5 text-[#6B6B6B] hover:text-[#111111]">
          <X className="w-5 h-5" />
        </button>

        <div className="p-5 md:p-10 pb-0 pr-14">
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">
            {TYPE_LABELS[quote.submissionType] || (quote.submissionType === 'prestige' ? 'Demande Prestige' : 'Demande Célébration')}
          </span>
          <h3 className="text-3xl font-serif text-[#111111]">{quote.firstName} {quote.lastName}</h3>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#6B6B6B]">
            <span className="flex items-center gap-2"><Mail className="w-4 h-4" /> <a href={`mailto:${quote.email}`} className="hover:text-[#6B6B6B]">{quote.email}</a></span>
            {quote.phone && <span className="flex items-center gap-2"><Phone className="w-4 h-4" /> {quote.phone}</span>}
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Reçu le {formatDate(quote.createdAt)}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#111111]/10 mt-8 px-5 md:px-10 flex gap-2 overflow-x-auto sticky top-0 bg-white z-10">
          {[
            { id: 'details', label: 'Détails' },
            { id: 'notes', label: `Notes (${(quote.notes || []).length})` },
            { id: 'reply', label: 'Répondre' },
            { id: 'history', label: `Historique (${(quote.replies || []).length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`quote-tab-${t.id}`}
              className={`px-4 py-3 text-[10px] uppercase tracking-[0.3em] border-b-2 transition-colors ${
                tab === t.id ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#6B6B6B] hover:text-[#111111]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 md:p-10">
          {tab === 'details' && <DetailsTab quote={quote} />}
          {tab === 'notes' && <NotesTab quote={quote} onUpdate={onUpdate} />}
          {tab === 'reply' && <ReplyTab quote={quote} onUpdate={onUpdate} emailConfigured={emailConfigured} />}
          {tab === 'history' && <HistoryTab quote={quote} />}

          <div className="border-t border-[#111111]/10 mt-10 pt-6 flex flex-wrap justify-between items-center gap-4">
            <select
              value={quote.status}
              onChange={(e) => onStatusChange(e.target.value)}
              data-testid="quote-detail-status"
              className={`px-4 py-2 text-xs uppercase tracking-widest border-0 ${STATUS_COLORS[quote.status] || 'bg-gray-100'}`}
            >
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button onClick={onDelete} data-testid="quote-detail-delete" className="text-xs uppercase tracking-[0.3em] text-red-500 hover:text-red-700 flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================== Tabs ============================== */

const fmtAnswer = (v) => (Array.isArray(v) ? v.join(', ') : v === true ? 'Oui' : v === false ? 'Non' : v);

const AnswersList = ({ answers }) => (
  <div className="space-y-8" data-testid="quote-answers">
    {[...new Set(answers.map((a) => a.section))].map((sec) => (
      <div key={sec}>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-3 border-b border-[#111111]/10 pb-2">{sec}</p>
        <dl className="space-y-3">
          {answers.filter((a) => a.section === sec).map((a) => (
            <div key={a.label} className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-1 md:gap-4 text-sm">
              <dt className="text-[#6B6B6B]">{a.label}</dt>
              <dd className="text-[#111111] whitespace-pre-line break-words">
                {a.value && Array.isArray(a.value) && a.value.some((u) => typeof u === 'string' && u.startsWith('/api/files/'))
                  ? a.value.map((u) => <a key={u} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noopener noreferrer" className="block underline">{u.split('/').pop()}</a>)
                  : fmtAnswer(a.value) || '—'}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    ))}
  </div>
);

const DetailsTab = ({ quote }) => (Array.isArray(quote.answers) && quote.answers.length > 0 ? <AnswersList answers={quote.answers} /> : (
  <>
    <div className="grid grid-cols-2 gap-6 mb-8">
      <Info label="Type d'événement" value={quote.eventType} />
      <Info label="Date souhaitée" value={quote.date} />
      <Info label="Heure" value={quote.time} />
      <Info label="Convives" value={quote.guests} />
      <Info label="Lieu" value={quote.location || quote.venueName || quote.address} />
      <Info label="Type de lieu" value={Array.isArray(quote.venueType) ? quote.venueType.join(', ') : quote.venueType} />
      <Info label="Lieu réservé" value={quote.venueBooked === true ? 'Oui' : quote.venueBooked === false ? 'Non' : null} />
      <Info label="Durée accompagnement" value={quote.duration} />
      <Info label="Budget" value={quote.budget} />
      <Info label="Couleurs / Style" value={quote.colors} />
      <Info label="Ambiance" value={quote.ambiance} />
      <Info label="Instagram" value={quote.instagram} />
      <Info label="Préférence contact" value={quote.contactPref} />
    </div>

    {quote.options && Object.values(quote.options).some(Boolean) && (
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-3">Options choisies</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(quote.options).filter(([, v]) => v).map(([k]) => (
            <span key={k} className="text-xs px-3 py-1 border border-[#111111] text-[#111111] font-serif">{k}</span>
          ))}
        </div>
      </div>
    )}

    {quote.services && Object.values(quote.services).some(Boolean) && (
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-3">Services souhaités</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(quote.services).filter(([, v]) => v).map(([k]) => (
            <span key={k} className="text-xs px-3 py-1 border border-[#111111] text-[#111111] font-serif">{k}</span>
          ))}
        </div>
      </div>
    )}

    {(quote.message || quote.vision || quote.inspirations) && (
      <div className="border-t border-[#111111]/10 pt-6 space-y-5">
        {quote.message && <Block label="Message" text={quote.message} />}
        {quote.vision && <Block label="Univers recherché" text={quote.vision} />}
        {quote.inspirations && <Block label="Inspirations" text={quote.inspirations} />}
      </div>
    )}

    {Array.isArray(quote.inspirationFiles) && quote.inspirationFiles.length > 0 && (
      <div className="border-t border-[#111111]/10 pt-6 mt-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-3">Photos d'inspiration ({quote.inspirationFiles.length})</p>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {quote.inspirationFiles.map((url, idx) => (
            <a key={idx} href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer" className="block border border-[#111111]/10 bg-[#F5F5F5] hover:border-[#111111] transition-colors">
              <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-24 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            </a>
          ))}
        </div>
      </div>
    )}
  </>
));

const NotesTab = ({ quote, onUpdate }) => {
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setAdding(true);
    try {
      await api.post(`/admin/quotes/${quote.id}/notes`, { text });
      const { data } = await api.get('/admin/quotes');
      const updated = data.find((q) => q.id === quote.id);
      if (updated) onUpdate(updated);
      setText('');
      toast.success('Note ajoutée');
    } catch {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setAdding(false);
    }
  };

  const del = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await api.delete(`/admin/quotes/${quote.id}/notes/${noteId}`);
      const { data } = await api.get('/admin/quotes');
      const updated = data.find((q) => q.id === quote.id);
      if (updated) onUpdate(updated);
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div data-testid="notes-tab">
      {(quote.notes || []).length === 0 ? (
        <p className="text-center text-[#6B6B6B] py-8 italic">Aucune note interne pour cette demande.</p>
      ) : (
        <div className="space-y-3 mb-6">
          {quote.notes.map((n) => (
            <div key={n.id} className="border border-[#111111]/10 bg-[#F5F5F5]/40 p-5 group" data-testid={`note-${n.id}`}>
              <div className="flex justify-between items-start gap-3 mb-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B]">
                  {n.author} · {formatDate(n.createdAt)}
                </span>
                <button onClick={() => del(n.id)} className="text-[#6B6B6B] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-[#111111] whitespace-pre-wrap leading-relaxed">{n.text}</p>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={add} className="border-t border-[#111111]/10 pt-6">
        <label className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-3 block">Ajouter une note interne</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Visible uniquement par l'équipe admin…"
          className="w-full border border-[#111111]/20 bg-white px-4 py-3 text-sm font-serif focus:border-[#111111] focus:outline-none transition-colors"
          data-testid="note-input"
        />
        <button
          type="submit"
          disabled={adding || !text.trim()}
          data-testid="note-submit"
          className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors disabled:opacity-50"
        >
          <MessageSquarePlus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </form>
    </div>
  );
};

const ReplyTab = ({ quote, onUpdate, emailConfigured }) => {
  const defaultSubject = quote.submissionType === 'prestige'
    ? `Votre demande prestige — JBMH Event`
    : `Votre demande de devis — JBMH Event`;
  const defaultBody = `Bonjour ${quote.firstName || quote.lastName},

Merci pour votre demande concernant ${quote.eventType || 'votre événement'}.

`;
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!emailConfigured) {
      toast.error('Configurez RESEND_API_KEY dans les secrets pour activer l\'envoi.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/admin/quotes/${quote.id}/reply`, { subject, body });
      const { data } = await api.get('/admin/quotes');
      const updated = data.find((q) => q.id === quote.id);
      if (updated) onUpdate(updated);
      toast.success(`Email envoyé à ${quote.email}`);
      setBody(defaultBody);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={send} data-testid="reply-tab" className="space-y-4">
      <div className="text-xs text-[#6B6B6B] mb-3">
        Destinataire : <span className="font-serif text-[#111111]">{quote.email}</span>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-2 block">Sujet</label>
        <input
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border border-[#111111]/20 bg-white px-4 py-3 text-sm font-serif focus:border-[#111111] focus:outline-none transition-colors"
          data-testid="reply-subject"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-2 block">Message</label>
        <textarea
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          className="w-full border border-[#111111]/20 bg-white px-4 py-3 text-sm font-serif focus:border-[#111111] focus:outline-none transition-colors leading-relaxed"
          data-testid="reply-body"
        />
        <p className="text-[10px] text-[#6B6B6B] mt-1 italic">
          Le message sera mis en forme dans un template HTML élégant aux couleurs JBMH.
        </p>
      </div>
      <button
        type="submit"
        disabled={sending || !emailConfigured}
        data-testid="reply-send"
        className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors disabled:opacity-40"
      >
        <Send className="w-3.5 h-3.5" /> {sending ? 'Envoi…' : emailConfigured ? "Envoyer l'email" : 'Resend non configuré'}
      </button>
    </form>
  );
};

const HistoryTab = ({ quote }) => {
  if (!quote.replies || quote.replies.length === 0) {
    return <p className="text-center text-[#6B6B6B] py-8 italic">Aucune réponse envoyée pour cette demande.</p>;
  }
  return (
    <div className="space-y-4" data-testid="history-tab">
      {quote.replies.map((r) => (
        <div key={r.id} className="border border-[#111111]/10 bg-white p-5" data-testid={`reply-${r.id}`}>
          <div className="flex justify-between items-start gap-3 mb-3">
            <div>
              <p className="font-serif text-base text-[#111111]">{r.subject}</p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mt-1">
                Envoyé à {r.recipient} · {formatDate(r.sentAt)} par {r.sentBy}
              </p>
            </div>
          </div>
          <p className="text-sm text-[#111111] whitespace-pre-wrap leading-relaxed border-t border-[#111111]/10 pt-3">{r.body}</p>
        </div>
      ))}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-1">{label}</p>
    <p className="text-sm font-serif text-[#111111]">{value || '—'}</p>
  </div>
);

const Block = ({ label, text }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.3em] text-[#111111] mb-2">{label}</p>
    <p className="text-sm text-[#111111] leading-relaxed whitespace-pre-wrap">{text}</p>
  </div>
);

export default QuotesManager;
